import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Check, Monitor, ExternalLink } from "lucide-react";

export default function FlightAdmin() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/flight/tokens");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load display tokens");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/admin/flight/tokens", { name, location });
      setName(""); setLocation("");
      toast.success("Display token created");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this display token? The screen will stop showing new data.")) return;
    try {
      await api.delete(`/admin/flight/tokens/${id}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const publicUrl = (tok) => `${window.location.origin}/display/flight/${tok}`;

  const copy = async (id, tok) => {
    try {
      await navigator.clipboard.writeText(publicUrl(tok));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="flight-admin">
      <div className="flex items-center gap-3 mb-6">
        <Monitor className="w-6 h-6 text-[#005289]" />
        <div>
          <h1 className="text-xl font-bold">Flight Status Display</h1>
          <p className="text-sm text-slate-500">No-login rotating wall display of contracts as they go live. Restricted contracts and dollar amounts are never shown.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-5">
        <div className="font-semibold mb-3">Create a new display token</div>
        <div className="grid grid-cols-3 gap-3">
          <input className="h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="Display name (e.g., Main Lobby TV)" value={name} onChange={e => setName(e.target.value)} data-testid="flight-name" />
          <input className="h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="Location (e.g., HR floor 2)" value={location} onChange={e => setLocation(e.target.value)} data-testid="flight-location" />
          <button className="h-9 px-4 bg-[#005289] text-white text-sm font-semibold rounded-md hover:bg-[#003d6a] flex items-center gap-2" onClick={create} data-testid="flight-create">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-md font-semibold">Active display tokens</div>
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">No display tokens yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Location</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Views</th>
                <th className="text-left px-4 py-2">Public URL</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`flight-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.location || "—"}</td>
                  <td className="px-4 py-2">
                    {r.active ? <span className="text-emerald-700 text-xs bg-emerald-50 px-2 py-0.5 rounded">Active</span>
                              : <span className="text-red-700 text-xs bg-red-50 px-2 py-0.5 rounded">Revoked</span>}
                  </td>
                  <td className="px-4 py-2">{r.views || 0}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-[280px] truncate">{publicUrl(r.token)}</td>
                  <td className="px-4 py-2 text-right">
                    <button className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 mr-2" onClick={() => copy(r.id, r.token)} data-testid={`flight-copy-${r.id}`}>
                      {copiedId === r.id ? <Check className="w-3 h-3 text-emerald-600 inline" /> : <Copy className="w-3 h-3 inline" />}
                    </button>
                    <a className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 mr-2 inline-flex items-center gap-1" href={publicUrl(r.token)} target="_blank" rel="noreferrer" data-testid={`flight-open-${r.id}`}>
                      <ExternalLink className="w-3 h-3" /> Open
                    </a>
                    {r.active && (
                      <button className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50" onClick={() => revoke(r.id)} data-testid={`flight-revoke-${r.id}`}>
                        <Trash2 className="w-3 h-3 inline" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
